import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import useSWR from "swr";
import { saveAs } from "file-saver";
import ReactGA from "react-ga4";
import { END_POINTS } from "constants/APIConstant.js";
import { API_BASE_URL } from "configs/AppConfig.js";
import {
  ALL_HALFS,
  ALL_MONTHS,
  ALL_QUARTERS,
  buildSearchUrl,
  filterPeriodOptions,
  getPeriodFieldIds,
  getPeriodsList,
  getUniqueYears,
  mapSearchDetails,
} from "./utils";

export function useVersionsData() {
  const dir = useSelector(({ theme }) => theme.direction);
  const currentLocale = useSelector(({ theme }) => theme.locale);
  const { id } = useParams();

  const [preparingPDFFile, setPreparingPDFFile] = useState(false);
  const [preparingExcelFile, setPreparingExcelFile] = useState(false);
  const [showPdfFiles, setShowPdfFiles] = useState(false);
  const [showExcelFiles, setShowExcelFiles] = useState(false);

  const dataFetching = useSWR({
    url: `${END_POINTS.PUBLICATION_DETAILS}/${id}`,
  });

  const relatedDataFetching = useSWR({
    url: `${END_POINTS.RELATED_PUBLICATION_DETAILS}=${id}`,
  });

  const periodsFetching = useSWR({
    url: `${END_POINTS.PUBLICATION_PERIODS}=${id}`,
  });

  const publication = dataFetching?.data?.data;
  const publicationDetail = publication?.publicationDetail;

  const [selectYear, setSelectYear] = useState(publicationDetail?.year);
  const [selectMonth, setSelectMonth] = useState(publicationDetail?.month);
  const [selectQuarter, setSelectQuarter] = useState(
    publicationDetail?.quarter
  );

  const periods = useMemo(
    () => getPeriodsList(periodsFetching),
    [
      periodsFetching.data,
      periodsFetching.error,
      periodsFetching.isLoading,
    ]
  );

  const [currentYearMonthIds, setCurrentYearMonthIds] = useState([]);
  const [currentYearQuarterIds, setCurrentYearQuarterIds] = useState([]);

  const uniqueRenderYears = useMemo(
    () => getUniqueYears(periods),
    [periods]
  );

  const renderedMonths = useMemo(
    () =>
      currentYearMonthIds.length > 0
        ? filterPeriodOptions(ALL_MONTHS, currentYearMonthIds)
        : [],
    [currentYearMonthIds]
  );

  const renderedQuarters = useMemo(
    () =>
      currentYearQuarterIds.length > 0
        ? filterPeriodOptions(ALL_QUARTERS, currentYearQuarterIds)
        : [],
    [currentYearQuarterIds]
  );

  const renderedHalfs = useMemo(
    () =>
      currentYearQuarterIds.length > 0
        ? filterPeriodOptions(ALL_HALFS, currentYearQuarterIds)
        : [],
    [currentYearQuarterIds]
  );

  const searchPublicationDetailsFetching = useSWR({
    url: buildSearchUrl(END_POINTS.SEARCH_PUBLICATION_DETAILS, id, {
      selectYear,
      selectMonth,
      selectQuarter,
      currentYearMonthIds,
      currentYearQuarterIds,
    }),
  });

  const searchDetails = useMemo(
    () => mapSearchDetails(searchPublicationDetailsFetching),
    [
      searchPublicationDetailsFetching.data,
      searchPublicationDetailsFetching.error,
      searchPublicationDetailsFetching.isLoading,
    ]
  );

  useEffect(() => {
    ReactGA.initialize(END_POINTS.TRACKING_ID);
  }, []);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: `/publications/${id}`,
      title: `Publication: ${publication?.name}-${publicationDetail?.year}`,
    });
  }, [id]);

  useEffect(() => {
    setSelectYear(publicationDetail?.year);
    setSelectMonth(publicationDetail?.month);
    setSelectQuarter(publicationDetail?.quarter);
  }, [id, publication]);

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeOut);
  }, [
    id,
    selectYear,
    selectMonth,
    selectQuarter,
    currentYearMonthIds,
    currentYearQuarterIds,
  ]);

  useEffect(() => {
    setCurrentYearMonthIds(getPeriodFieldIds(periods, selectYear, "month"));
    setSelectMonth(publicationDetail?.month);
    setCurrentYearQuarterIds(
      getPeriodFieldIds(periods, selectYear, "quarter")
    );
    setSelectQuarter(publicationDetail?.quarter);
  }, [selectYear, publication, periods]);

  const onExport = async (fileType, url, publicationName) => {
    if (fileType === "PDF") {
      setPreparingPDFFile(true);
    } else {
      setPreparingExcelFile(true);
    }

    await fetch(
      `${API_BASE_URL}/api/MediaUploader/download?url=${url}&fileName=${publicationName}`
    ).then((response) => {
      response.blob().then((blob) => {
        saveAs(blob, publicationName);
      });
    });

    ReactGA.event({
      category: "Download",
      action: "Download Publication",
      label: `${publicationName}`,
      file_type: fileType,
      bulletin_id: id,
    });

    setPreparingPDFFile(false);
    setPreparingExcelFile(false);
  };

  const handleYearChange = (year) => {
    setSelectYear(year);
    setSelectMonth(-1);
    setSelectQuarter(-1);
    setCurrentYearMonthIds(getPeriodFieldIds(periods, year, "month"));
    setCurrentYearQuarterIds(getPeriodFieldIds(periods, year, "quarter"));
  };

  const isLoading =
    dataFetching.isLoading ||
    relatedDataFetching.isLoading ||
    periodsFetching.isLoading;

  return {
    id,
    dir,
    currentLocale,
    isLoading,
    publication,
    relatedPublications: relatedDataFetching?.data?.data || [],
    selectYear,
    selectMonth,
    selectQuarter,
    setSelectMonth,
    setSelectQuarter,
    handleYearChange,
    uniqueRenderYears,
    renderedMonths,
    renderedQuarters,
    renderedHalfs,
    searchDetails,
    preparingPDFFile,
    preparingExcelFile,
    showPdfFiles,
    showExcelFiles,
    setShowPdfFiles,
    setShowExcelFiles,
    onExport,
  };
}
